'use client';

import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Stack, Button, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';

export default function HomeKeepTrack() {
  const t = useTranslations('Home.keepTrack');

  const BADGES = [t('badges.individualReports'), t('badges.history'), t('badges.compareNorms')];

  return (
    <Box
      component="section"
      sx={(theme) => ({
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
        overflow: 'hidden',
      })}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          minHeight: { md: 560 },
        }}
      >
        {/* Left content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: { xs: 7, md: 12 },
            px: { xs: 3, sm: 5, md: 8, lg: 10 },
          }}
        >
          <Box sx={{ maxWidth: 540 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: 'primary.main',
                mb: 1.5,
              }}
            >
              {t('overline')}
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(28px, 3.5vw, 42px)',
                lineHeight: 1.1,
                letterSpacing: '-1px',
                color: 'text.primary',
                mb: 2.5,
              }}
            >
              {t('title')}
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'text.secondary',
                mb: 5,
                textWrap: 'pretty',
              }}
            >
              {t('description')}
            </Typography>

            <Stack spacing={1.25} sx={{ mb: 5 }}>
              {BADGES.map((label) => (
                <Stack key={label} direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Iconify icon="eva:checkmark-fill" width={12} sx={{ color: 'common.white' }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>

            <Button
              href={paths.contact}
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<Iconify icon="ic:round-arrow-right-alt" />}
              sx={{
                px: 4,
                py: 1.75,
                borderRadius: 1.25,
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.secondary.main, 0.35)}`,
                '&:hover': {
                  boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.secondary.main, 0.45)}`,
                },
              }}
            >
              {t('contactUs')}
            </Button>
          </Box>
        </Box>

        {/* Right SVG mockup */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 320, md: 560 },
            bgcolor: '#1a2636',
            px: { xs: 3, md: 4 },
            py: { xs: 5, md: 4 },
          }}
        >
          <Box
            component="img"
            src="/assets/images/home/keeptrack.png"
            alt="Spelarrapport — Testelope dashboard"
            sx={{ width: '100%', maxWidth: 620, height: 'auto', display: 'block' }}
          />
        </Box>
      </Box>
    </Box>
  );
}
